/**
 * Listado del back office editorial.
 *
 * Dibujado con `YoolTable` del Design System Yool, que trae buscador, filtros y
 * orden en cliente: por eso este archivo ya NO tiene estado de `busqueda` ni de
 * `filtroEstado` — los hacia a mano y ahora los hace la tabla.
 *
 * TRAMPA DEL FILTRO, y es la que deja el listado vacio si se ignora: las filas
 * tienen que traer los valores CRUDOS de `estado` y `categoria`, porque el
 * filtro compara `String(row[key])`. Lo bonito se pinta en el `render` de la
 * columna. Si la fila trae el badge en vez del valor, el filtro no encuentra
 * nada.
 *
 * Se conserva la logica de defensa del archivo original: `productoCaido`, el
 * borrado en dos pasos con confirmacion, y `logError` + `toast.error` en cada
 * fallo de carga. Sin bundle del DS, cae a una tabla simple.
 *
 * Spec: docs/superpowers/specs/2026-08-26-editorial-design-system-design.md
 */
import React, { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@clerk/clerk-react";
import { toast } from "sonner";
// Solo para la caída sin bundle: con el bundle las acciones son un
// `YoolButton` de texto y el `IconButton` del sistema. "Destacada" dejó de ser
// una estrella y pasó a ser un badge, así que `Star` ya no se usa.
import { Pencil, Trash2 } from "lucide-react";
import { logError } from "../../lib/utils";
import { listar, eliminar } from "../../services/historiasApi";
import { PageHead, FormCard, Pill } from "../../components/product_config/formkit";
import {
  RowActions as RowActionsLocal,
  ActionButton,
  StatusPill as StatusPillLocal,
} from "../../components/product_config/GroupedTable";

const DS = () =>
  (typeof window !== "undefined" && window.DesignSystemRemix_1a4bd3) || {};

/**
 * Base del sitio público, para poder abrir la historia publicada desde acá.
 *
 * Se reusa `REACT_APP_FLOW_URL`, que ya es la convención del repo
 * (`components/tabs/calculator/simulador/SimuladorSection.jsx`), en vez de
 * inventar una variable nueva: cada entorno de tadmin apunta al flow que le
 * corresponde y esto lo sigue solo. Sin definir, el slug se dibuja como texto
 * y no como un enlace roto.
 */
const FLOW_URL = (process.env.REACT_APP_FLOW_URL || "").replace(/\/+$/, "");

/**
 * La URL pública de una historia, o `null` si no hay ninguna a la que ir.
 *
 * SOLO LAS PUBLICADAS. Las consultas de flow filtran `estado = 'publicada'`
 * (src/lib/historias/consultas.ts), asi que el enlace de un borrador seria un
 * 404 — peor que no tener enlace, porque promete una pagina que no existe.
 */
function urlPublica(r) {
  if (!FLOW_URL || !r.slug || r.estado !== "publicada") return null;
  return `${FLOW_URL}/historias/${r.slug}`;
}

/** Spec §9: si el producto principal está archivado o inactivo, en flow se cae
    su banda y su CTA. El listado lo avisa en vez de dejarlo pasar callado.

    Se conserva este criterio y NO el del template, que resuelve `caido` como
    "el producto principal no está en el catálogo". Ahí el catálogo es una
    constante de demo; acá `listar()` trae `producto:producto(nombre, activo,
    archivado)` embebido, así que se puede distinguir el caso real —producto
    que existe pero está inactivo o archivado— del producto inexistente. El
    template no tenía con qué. */
function productoCaido(historia) {
  const principal = (historia.productos || []).find((p) => p.principal);
  const p = principal?.producto;
  return Boolean(principal) && (!p || p.activo === false || p.archivado === true);
}

/**
 * Proyección plana de una historia a fila de tabla, tal como la define el
 * template (`fila(d)`).
 *
 * NO es cosmético: `YoolTable` filtra comparando `String(row[key])` y busca
 * sobre `row[key]` crudo, así que cada cosa por la que se filtra, se busca o
 * se ordena tiene que ser una clave PLANA de la fila. Por eso `bloques` y
 * `caido` se calculan una vez acá y no dentro del `render` de la columna: en
 * el render no serían ordenables.
 */
function fila(h) {
  return {
    id: h.id,
    nombre: h.nombre,
    /* La cita es lo que el lector ve: es el titular sobre la foto en el hero y
       el texto de la tarjeta del home. El `nombre` es de uso interno y no
       aparece en ningún lado del sitio, así que reconocer una historia por él
       obliga a abrirla. Va en la fila —y no solo en el render— porque es clave
       de búsqueda, y `YoolTable` busca sobre `row[key]` crudo. */
    quote: h.quote,
    slug: h.slug,
    categoria: h.categoria,
    estado: h.estado,
    destacada: h.destacada,
    fecha: h.fecha_publicacion,
    productos: (h.productos || []).length,
    caido: productoCaido(h),
    bloques: (h.cuerpo || []).filter((b) => String(b?.texto || "").trim()).length,
  };
}

export default function HistoriasList() {
  const { getToken } = useAuth();
  const navigate = useNavigate();
  const [historias, setHistorias] = useState([]);
  const [cargando, setCargando] = useState(true);
  // C3: el borrado es duro (delete real, arrastra historia_productos por FK)
  // e irreversible — no puede disparar de un solo click en el ícono.
  const [porEliminar, setPorEliminar] = useState(null);

  const cargar = useCallback(async () => {
    setCargando(true);
    try {
      setHistorias(await listar(await getToken()));
    } catch (e) {
      logError("No se pudieron cargar las historias:", e);
      toast.error(e.message);
    } finally {
      setCargando(false);
    }
  }, [getToken]);

  useEffect(() => { cargar(); }, [cargar]);

  const confirmarBorrado = async () => {
    if (!porEliminar) return;
    try {
      await eliminar(await getToken(), porEliminar.id);
      toast.success(`Historia "${porEliminar.nombre}" eliminada`);
      setPorEliminar(null);
      cargar();
    } catch (e) {
      logError("No se pudo eliminar la historia:", e);
      toast.error(e.message);
    }
  };

  const {
    YoolTable, YoolButton, YoolModal, YoolPageHeader, YoolEmptyState, YoolBadge,
  } = DS();

  // Los helpers de celda son parte de YoolTable.jsx en el DS, no piezas
  // aparte: el primitivo manda y `GroupedTable` queda solo como caida sin
  // bundle. Son clones casi identicos, asi que el cambio no se ve — lo que
  // cambia es de donde sale la definicion.
  const { StatusPill = StatusPillLocal, RowActions = RowActionsLocal } = DS();

  const { IconButton } = DS();

  /* El template resuelve las acciones de fila con "Editar" como `YoolButton`
     ghost de TEXTO y solo el borrado como ícono (`xCircle`, que sí está en
     `YOOL_ICON_NAMES`). No es una decisión estética: el set de 60 íconos del
     sistema no tiene `pencil` ni `trash`, así que un par de íconos para las
     dos acciones era inalcanzable. Con texto para la acción común y un ícono
     rojo para la destructiva, además, deja de hacer falta adivinar cuál lápiz
     es cuál.

     `ActionButton` (local, con los íconos de lucide) queda SOLO como caída
     sin bundle, igual que el resto del archivo. */
  const acciones = (r) => (
    <RowActions>
      {YoolButton ? (
        <YoolButton
          variant="ghost"
          size="sm"
          onClick={() => navigate(`/editorial/historias/edit/${r.id}`)}
        >
          Editar
        </YoolButton>
      ) : (
        <ActionButton icon={Pencil} title="Editar" to={`/editorial/historias/edit/${r.id}`} />
      )}
      {IconButton ? (
        <IconButton
          icon="xCircle"
          title={`Eliminar ${r.nombre || "historia"}`}
          danger
          onClick={() => setPorEliminar(r)}
        />
      ) : (
        <ActionButton icon={Trash2} title="Eliminar" danger onClick={() => setPorEliminar(r)} />
      )}
    </RowActions>
  );

  /* Columnas verbatim del template `EditorialHistorias.dc.html` (`columnas()`),
     incluidos los anchos: son los que hacen que "Historia" se quede con el
     38% y las seis columnas de dato no se peleen el resto. */
  const columnas = [
    {
      key: "nombre",
      label: "Historia",
      sortable: true,
      wrap: true,
      width: "38%",
      /* Tres líneas, de más interna a más pública: el `nombre` con el que se
         archiva, la CITA que el lector ve, y la URL donde vive.

         El slug baja debajo del título y la columna Slug desaparece: son la
         misma identidad, y separadas gastaban dos columnas para decirlo.
         "Destacada" pasa de un ícono de estrella sin etiqueta a un badge
         `recomendado` que se lee. */
      render: (r) => {
        const url = urlPublica(r);
        const ruta = r.slug ? `/historias/${r.slug}` : "sin slug";
        return (
          <div style={{ minWidth: 0, display: "grid", gap: 5 }}>
            <span
              style={{
                fontSize: 13.5, fontWeight: 600, letterSpacing: "-.01em",
                whiteSpace: "normal", textWrap: "pretty",
              }}
            >
              {r.nombre || "Sin título"}
            </span>

            {/* La cita, que es el titular real de la historia en el sitio.
                Acotada a dos líneas: hay citas de varios renglones y sin el
                tope una sola fila estiraba toda la tabla. */}
            {r.quote ? (
              <span
                style={{
                  fontSize: 13, lineHeight: 1.4,
                  color: "var(--yool-gray1, #54585A)",
                  display: "-webkit-box", WebkitLineClamp: 2,
                  WebkitBoxOrient: "vertical", overflow: "hidden",
                  textWrap: "pretty",
                }}
                title={r.quote}
              >
                {r.quote}
              </span>
            ) : null}

            <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
              {/* Enlace solo si la historia está publicada: ver `urlPublica`.
                  Un borrador se dibuja como texto, no como enlace a un 404.
                  `noopener noreferrer` porque abre en otra pestaña. */}
              {url ? (
                <a
                  href={url}
                  target="_blank"
                  rel="noopener noreferrer"
                  style={{
                    fontSize: 12, color: "var(--yool-cta, #007D6E)",
                    textDecoration: "none", display: "inline-flex",
                    alignItems: "center", gap: 4,
                  }}
                  title={`Abrir ${url} en una pestaña nueva`}
                >
                  {ruta}
                  <span aria-hidden="true">↗</span>
                </a>
              ) : (
                <span style={{ fontSize: 12, color: "var(--yool-muted, #8D8D8D)" }}>
                  {ruta}
                </span>
              )}
              {r.destacada &&
                (YoolBadge ? (
                  <YoolBadge variant="recomendado">Destacada</YoolBadge>
                ) : (
                  <Pill>Destacada</Pill>
                ))}
            </div>
          </div>
        );
      },
    },
    {
      key: "categoria",
      label: "Categoría",
      sortable: true,
      width: 120,
      render: (r) =>
        YoolBadge ? <YoolBadge variant="neutro">{r.categoria}</YoolBadge> : r.categoria,
    },
    {
      key: "estado",
      label: "Estado",
      sortable: true,
      width: 130,
      // `borrador` es una clave propia del PILL del DS, con su gris y su
      // etiqueta: no hace falta pasar por `inactivo` para conseguir el mismo
      // color. El texto sigue explicito porque "Publicada" no es "Activo".
      render: (r) => (
        <StatusPill estado={r.estado === "publicada" ? "activo" : "borrador"}>
          {r.estado === "publicada" ? "Publicada" : "Borrador"}
        </StatusPill>
      ),
    },
    {
      /* Columna nueva. Dice de un vistazo qué historias están escritas y
         cuáles son un cascarón con portada — antes había que abrir cada una.
         Cuenta solo los bloques CON texto, el mismo criterio con el que
         `haciaEntrada` decide qué se guarda. */
      key: "bloques",
      label: "Cuerpo",
      sortable: true,
      numeric: true,
      width: 110,
      align: "right",
      render: (r) => (
        <span
          style={{
            fontVariantNumeric: "tabular-nums",
            color: r.bloques ? "var(--yool-black, #1A1A1A)" : "var(--yool-muted, #8D8D8D)",
          }}
        >
          {r.bloques ? `${r.bloques} bloques` : "—"}
        </span>
      ),
    },
    {
      key: "productos",
      label: "Productos",
      sortable: true,
      numeric: true,
      width: 190,
      render: (r) => (
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <span style={{ fontVariantNumeric: "tabular-nums" }}>{r.productos}</span>
          {r.caido &&
            (YoolBadge ? (
              <YoolBadge variant="pendiente">no simulable</YoolBadge>
            ) : (
              <Pill tone="warn">no simulable</Pill>
            ))}
        </div>
      ),
    },
    {
      key: "fecha",
      label: "Publicación",
      sortable: true,
      width: 140,
      render: (r) => (
        <span
          style={{
            fontVariantNumeric: "tabular-nums",
            color: r.fecha ? "var(--yool-black, #1A1A1A)" : "var(--yool-muted, #8D8D8D)",
          }}
        >
          {r.fecha || "—"}
        </span>
      ),
    },
    { key: "_acc", label: "", align: "right", width: 150, render: acciones },
  ];

  const vacio = {
    icon: "document",
    title: "Todavía no hay historias",
    text: "Crea la primera y queda en borrador hasta que la publiques.",
  };

  /* La cita entra en la búsqueda por el mismo motivo por el que entra en la
     celda: es como se recuerda una historia. Buscar solo por el `nombre`
     interno obligaba a acordarse de cómo se archivó. */
  const buscador = {
    placeholder: "Buscar por título, cita o slug",
    keys: ["nombre", "quote", "slug"],
  };

  const filas = historias.map(fila);
  const publicadas = filas.filter((r) => r.estado === "publicada").length;
  /* El subtítulo cuenta lo que hay, en vez de repetir el título con otras
     palabras ("Contenido editorial publicado en flow" no decía nada que el
     título no dijera). Es el `resumenLista` del template. */
  const resumen = `${filas.length} historias · ${publicadas} publicadas · ${
    filas.length - publicadas
  } en borrador`;

  const nueva = YoolButton ? (
    <YoolButton variant="primary" onClick={() => navigate("/editorial/historias/new")}>
      Nueva historia
    </YoolButton>
  ) : null;

  return (
    <div>
      {YoolPageHeader ? (
        /* El botón va AL LADO del encabezado, no dentro de su slot `actions`:
           el template los pone en una fila `space-between` alineada a
           `flex-end`, así que el CTA queda a la altura del subtítulo y no
           flotando junto al título. */
        <div
          style={{
            display: "flex", alignItems: "flex-end", justifyContent: "space-between",
            gap: 24, flexWrap: "wrap", marginBlockEnd: 28,
          }}
        >
          <YoolPageHeader
            eyebrow="Editorial · Contenido"
            title="Historias"
            subtitle={cargando ? "Cargando…" : resumen}
          />
          {nueva}
        </div>
      ) : (
        <PageHead title="Historias" sub={cargando ? "Cargando…" : resumen} />
      )}

      {cargando && YoolEmptyState ? (
        <YoolEmptyState loading title="Cargando historias…" />
      ) : YoolTable ? (
        <YoolTable
          columns={columnas}
          rows={filas}
          rowKey="id"
          search={buscador}
          filters={[
            {
              key: "estado",
              label: "Todos los estados",
              options: [
                { value: "publicada", label: "Publicada" },
                { value: "borrador", label: "Borrador" },
              ],
            },
            {
              key: "categoria",
              label: "Todas las categorías",
              options: [
                { value: "B2C", label: "B2C — persona" },
                { value: "B2B", label: "B2B — empresa" },
              ],
            },
          ]}
          initialSort={{ key: "fecha", dir: "desc" }}
          empty={vacio}
        />
      ) : (
        // Sin bundle: tabla simple. No se reimplementan buscador ni filtros —
        // la pantalla sigue usable y el listado completo se ve igual.
        <FormCard>
          {cargando ? (
            <p>Cargando…</p>
          ) : filas.length === 0 ? (
            <p>{vacio.title}. {vacio.text}</p>
          ) : (
            <table>
              <thead>
                <tr>
                  {columnas.map((c) => (
                    <th key={c.key}>{c.label}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {/* Las filas proyectadas, no las historias crudas: los
                    `render` de las columnas leen `r.fecha`, `r.bloques` y
                    `r.caido`, que solo existen en la proyección. */}
                {filas.map((r) => (
                  <tr key={r.id}>
                    {columnas.map((c) => (
                      <td key={c.key}>{c.render ? c.render(r) : r[c.key]}</td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </FormCard>
      )}

      {YoolModal ? (
        <YoolModal
          open={Boolean(porEliminar)}
          onClose={() => setPorEliminar(null)}
          size="sm"
          title="Eliminar historia"
          desc={
            porEliminar
              ? `Se elimina "${porEliminar.nombre}" y sus productos asociados. No se puede deshacer.`
              : ""
          }
          footer={
            YoolButton ? (
              <>
                <YoolButton variant="ghost" onClick={() => setPorEliminar(null)}>
                  Cancelar
                </YoolButton>
                <YoolButton variant="destructive" onClick={confirmarBorrado}>
                  Eliminar
                </YoolButton>
              </>
            ) : null
          }
        />
      ) : (
        porEliminar && (
          <div role="dialog" aria-label="Eliminar historia">
            <p>
              Se elimina "{porEliminar.nombre}" y sus productos asociados. No se
              puede deshacer.
            </p>
            <button type="button" onClick={() => setPorEliminar(null)}>Cancelar</button>
            <button type="button" onClick={confirmarBorrado}>Eliminar</button>
          </div>
        )
      )}
    </div>
  );
}
